import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  CircularProgress,
  TextField,
  InputAdornment,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useDispatch, useSelector } from "react-redux";
import { alertActions } from "_store";
import {
  getStorageTrackingApi,
  postStorageTrackingApi,
  getStorageTrackingRangeApi,
  saveStorageTrackingRangeApi,
} from "services/storageTracking/storageTracking.thunks";
import { ERR_MSG } from "constants/labels";
import StorageTrackingTable from "./StorageTrackingTable";
import StorageTrackingUpload from "./StorageTrackingUpload";
import {
  formatThousands,
  formatNumberThousands,
  stripCommas,
  sanitizeDecimal,
} from "./StorageTrackingUtils";

const computeImbalance = (percent, totalStr) => {
  const value = ((Number(percent) || 0) / 100) * (Number(stripCommas(totalStr)) || 0);
  // Backend field is Int64 — must be a whole number, no decimals.
  return Math.round(value);
};

// Backend expects SystemLoadDate always as the first day of the month (yyyy-MM-01).
const toSystemLoadDate = (date) => dayjs(date).format("YYYY-MM-01");

const header = "Storage Tracking";

const SectionCard = ({ children, sx = {} }) => (
  <Box
    sx={{
      border: "1px solid #d6e4f0",
      borderRadius: "10px",
      p: 2,
      backgroundColor: "#FFFFFF",
      ...sx,
    }}
  >
    {children}
  </Box>
);

const StorageTracking = () => {
  const dispatch = useDispatch();

  const authUser = useSelector((x) => x.auth?.value);
  const user = authUser?.Data;
  const userAccess = user?.UserAccess;
  const isAdmin = userAccess?.some((access) => access.Role.toLowerCase().includes("admin"));

  const [systemLoadDate, setSystemLoadDate] = useState(dayjs().startOf("month"));
  const [totalStorages, setTotalStorages] = useState("");
  const [percentMin, setPercentMin] = useState("");
  const [percentMax, setPercentMax] = useState("");
  const [rows, setRows] = useState([]);
  const [isDataChanged, setIsDataChanged] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [isRangeEditMode, setIsRangeEditMode] = useState(false);
  const [rangeTotalStorages, setRangeTotalStorages] = useState("");
  const [rangeRows, setRangeRows] = useState([]);
  const [isRangeLoading, setIsRangeLoading] = useState(false);

  const buildMarketerRows = (marketerList) =>
    (marketerList ?? []).map((m) => ({
      MarketerName: m.CompanyName,
      percentOfSystemLoad: m.PercentSystemLoad ?? "",
    }));

  const fetchMarketerRows = async (date = systemLoadDate) => {
    setIsLoading(true);
    try {
      const result = await dispatch(getStorageTrackingApi(toSystemLoadDate(date))).unwrap();
      const { TotalStorageSCQ, SCQPercentRange, StorageTrackingByMarketerList } = result?.Data ?? {};

      setTotalStorages(formatThousands(String(TotalStorageSCQ ?? "")));
      setPercentMin(String(SCQPercentRange?.MinRange ?? ""));
      setPercentMax(String(SCQPercentRange?.MaxRange ?? ""));
      setRows(buildMarketerRows(StorageTrackingByMarketerList));
    } catch (error) {
      dispatch(alertActions.clear());
      dispatch(alertActions.error({ message: error.message ? ERR_MSG["073"] : error, header }));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRangeData = async () => {
    setIsRangeLoading(true);
    try {
      const result = await dispatch(getStorageTrackingRangeApi()).unwrap();
      const { TotalStorageSCQ, SCQPercentRanges } = result?.Data ?? {};

      setRangeTotalStorages(formatThousands(String(TotalStorageSCQ ?? "")));
      setRangeRows(
        (SCQPercentRanges ?? []).map((m) => ({
          MonthRange: m.MonthRange,
          MinRange: String(m.MinRange ?? ""),
          MaxRange: String(m.MaxRange ?? ""),
        }))
      );
    } catch (error) {
      dispatch(alertActions.clear());
      dispatch(alertActions.error({ message: error.message ? ERR_MSG["073"] : error, header }));
    } finally {
      setIsRangeLoading(false);
    }
  };

  useEffect(() => {
    fetchMarketerRows();
  }, []);

  const handleDateChange = (value) => {
    if (!value || !dayjs(value).isValid()) return;
    const date = dayjs(value).startOf("month");
    setSystemLoadDate(date);
    setIsDataChanged(false);
    fetchMarketerRows(date);
  };

  const handleOpenRangeEdit = () => {
    setIsRangeEditMode(true);
    fetchRangeData();
  };

  const handleCancelRangeEdit = () => {
    setIsRangeEditMode(false);
  };

  const handleRangeTotalStoragesChange = (e) => {
    const cleaned = stripCommas(e.target.value).replace(/[^0-9]/g, "");
    setRangeTotalStorages(formatThousands(cleaned));
  };

  const handleRangeRowChange = (index, field, value) => {
    setRangeRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: sanitizeDecimal(value) };
      return updated;
    });
  };

  const handleSaveRangeEdit = async () => {
    dispatch(alertActions.clear());

    if (!rangeTotalStorages) {
      dispatch(alertActions.error({ message: "Total Storages is required.", header }));
      return;
    }

    for (const r of rangeRows) {
      if (r.MinRange !== "" && r.MaxRange !== "" && Number(r.MinRange) > Number(r.MaxRange)) {
        dispatch(
          alertActions.error({
            message: `Percentage Range minimum cannot exceed maximum for ${r.MonthRange}.`,
            header,
          })
        );
        return;
      }
    }

    const payload = {
      TotalStorageSCQ: Number(stripCommas(rangeTotalStorages)) || 0,
      StorageTrackingSCQPercentPerMonth: rangeRows.map((r) => ({
        MonthRange: r.MonthRange,
        MinRange: Number(r.MinRange) || 0,
        MaxRange: Number(r.MaxRange) || 0,
      })),
    };

    try {
      const result = await dispatch(saveStorageTrackingRangeApi(payload)).unwrap();
      dispatch(
        alertActions.success({ message: result?.Message || "Storage Tracking range saved successfully.", header })
      );
      setIsRangeEditMode(false);
      await Promise.all([fetchMarketerRows(), fetchRangeData()]);
    } catch (error) {
      dispatch(alertActions.clear());
      dispatch(alertActions.error({ message: error.message ? ERR_MSG["073"] : error, header }));
    }
  };

  const handleRowChange = (rowIndex, field, value) => {
    setRows((prev) => {
      const updated = [...prev];
      updated[rowIndex] = { ...updated[rowIndex], [field]: value };
      return updated;
    });
    setIsDataChanged(true);
  };

  const displayRows = useMemo(
    () =>
      rows.map((r) => ({
        ...r,
        imbalanceAccountSCQ: formatNumberThousands(
          computeImbalance(r.percentOfSystemLoad, totalStorages)
        ),
      })),
    [rows, totalStorages]
  );

  const handleCancel = () => {
    setIsDataChanged(false);
    fetchMarketerRows();
  };

  const handleSave = async () => {
    dispatch(alertActions.clear());

    const payload = {
      SystemLoadDate: toSystemLoadDate(systemLoadDate),
      StorageTrackingByMarketerList: rows.map((r) => ({
        CompanyName: r.MarketerName,
        PercentSystemLoad: Number(r.percentOfSystemLoad) || 0,
        ImbalanceAccountSCQ: computeImbalance(r.percentOfSystemLoad, totalStorages),
      })),
    };

    try {
      const result = await dispatch(postStorageTrackingApi(payload)).unwrap();
      dispatch(alertActions.success({ message: result?.Message || "Storage Tracking saved successfully.", header }));
      setIsDataChanged(false);
      await Promise.all([fetchMarketerRows(), fetchRangeData()]);
    } catch (error) {
      dispatch(alertActions.clear());
      dispatch(alertActions.error({ message: error.message ? ERR_MSG["073"] : error, header }));
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", py: 3, opacity: isLoading ? 0.5 : 1, transition: "opacity 0.2s" }}>
      {isLoading && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255,255,255,0.55)",
            backdropFilter: "blur(2px)",
          }}
        >
          <CircularProgress size={48} sx={{ color: "#1a3a5c" }} />
        </Box>
      )}

      <SectionCard sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 1 }}>
          <Button
            size="small"
            onClick={handleOpenRangeEdit}
            disabled={isRangeEditMode}
            sx={{ minWidth: "auto" }}
            aria-label="Edit Total Storages settings"
          >
            <EditIcon fontSize="small" />
          </Button>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography sx={{ fontWeight: 700, color: "#1a3a5c", mb: 1 }}>
              Total Storages
            </Typography>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              value={totalStorages}
              disabled
              placeholder="0"
              inputProps={{ inputMode: "numeric" }}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <Typography sx={{ fontWeight: 700, color: "#1a3a5c", mb: 1 }}>
              Percentage Range (Min)
            </Typography>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              value={percentMin}
              disabled
              placeholder="0"
              inputProps={{ inputMode: "decimal" }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <Typography sx={{ fontWeight: 700, color: "#1a3a5c", mb: 1 }}>
              Percentage Range (Max)
            </Typography>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              value={percentMax}
              disabled
              placeholder="0"
              inputProps={{ inputMode: "decimal" }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
          </Grid>
        </Grid>
      </SectionCard>

      {isRangeEditMode && (
        <SectionCard sx={{ mb: 2, opacity: isRangeLoading ? 0.5 : 1, transition: "opacity 0.2s" }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontWeight: 700, color: "#1a3a5c", mb: 1 }}>
                Total Storages
              </Typography>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                value={rangeTotalStorages}
                onChange={handleRangeTotalStoragesChange}
                placeholder="0"
                inputProps={{ inputMode: "numeric" }}
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              mt: 1,
              border: "1px solid #e0e7ef",
              borderRadius: "10px",
              overflow: "hidden",
            }}
          >
            <Grid container spacing={0}>
              <Grid item xs={12} sm={4} sx={{ borderRight: { sm: "1px solid rgba(255,255,255,0.12)" } }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#fff",
                    backgroundColor: "#1a3a5c",
                    textAlign: "center",
                    py: 1.5,
                    px: 2,
                  }}
                >
                  Month
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4} sx={{ borderRight: { sm: "1px solid rgba(255,255,255,0.12)" } }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#fff",
                    backgroundColor: "#1a3a5c",
                    textAlign: "center",
                    py: 1.5,
                    px: 2,
                  }}
                >
                  Percentage Range (Min)
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#fff",
                    backgroundColor: "#1a3a5c",
                    textAlign: "center",
                    py: 1.5,
                    px: 2,
                  }}
                >
                  Percentage Range (Max)
                </Typography>
              </Grid>
            </Grid>

            {rangeRows.map((r, idx) => (
              <Grid
                container
                spacing={0}
                key={r.MonthRange ?? idx}
                sx={{
                  borderTop: "1px solid #e0e7ef",
                }}
              >
                <Grid
                  item
                  xs={12}
                  sm={4}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRight: { sm: "1px solid #e0e7ef" },
                    px: 2,
                    py: 1,
                  }}
                >
                  <Typography
                    sx={{
                      color: "#1a3a5c",
                      fontWeight: 600,
                    }}
                  >
                    {r.MonthRange}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={6}
                  sm={4}
                  sx={{ borderRight: { sm: "1px solid #e0e7ef" }, px: 2, py: 1 }}
                >
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    value={r.MinRange}
                    onChange={(e) => handleRangeRowChange(idx, "MinRange", e.target.value)}
                    placeholder="0"
                    inputProps={{ inputMode: "decimal" }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={6} sm={4} sx={{ px: 2, py: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    value={r.MaxRange}
                    onChange={(e) => handleRangeRowChange(idx, "MaxRange", e.target.value)}
                    placeholder="0"
                    inputProps={{ inputMode: "decimal" }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                  />
                </Grid>
              </Grid>
            ))}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
            <Button variant="contained" color="red" className="cancelbutton" onClick={handleCancelRangeEdit}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" className="submitbutton" onClick={handleSaveRangeEdit}>
              Save
            </Button>
          </Box>
        </SectionCard>
      )}

      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} md={8}>
          <SectionCard>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
                mb: 1,
              }}
            >
              <Typography sx={{ fontWeight: 700, color: "#1a3a5c" }}>
                Marketer Storage Breakdown
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Month"
                  views={["year", "month"]}
                  openTo="month"
                  format="MMMM YYYY"
                  value={systemLoadDate}
                  onChange={handleDateChange}
                  slotProps={{ textField: { size: "small" } }}
                />
              </LocalizationProvider>
            </Box>
            <StorageTrackingTable rows={displayRows} onRowChange={handleRowChange} isAdmin={isAdmin} />
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <StorageTrackingUpload
            rows={rows}
            setRows={setRows}
            setIsDataChanged={setIsDataChanged}
            isAdmin={isAdmin}
          />
        </Grid>
      </Grid>

      <Grid item xs={12} className="Personal-Information">
        <Button variant="contained" color="red" className="cancelbutton" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          className="submitbutton"
          onClick={handleSave}
          disabled={!isDataChanged}
        >
          Save
        </Button>
      </Grid>
    </Box>
  );
};

export default StorageTracking;
